import { Link } from "react-router-dom";
import { festival } from "../data/festival";
import { lineup2026 } from "../data/lineup";
import { pressQuotes } from "../data/press";
import { useTickets } from "../context/TicketsContext";
import Button from "../components/Button";
import { IconArrow } from "../components/icons";
import { Mushroom, Hummingbird, SnakeStripe, SunBurst, Squiggle } from "../components/Doodles";
import styles from "./Home.module.css";

export default function Home() {
  const headliners = lineup2026.filter((a) => a.headliner);
  const supporting = lineup2026.filter((a) => !a.headliner);
  const { open: openTickets } = useTickets();
  return (
    <>
      {/* HERO — save-the-date art is wide-format, so it owns the
          fold; the poster gets its own reveal section below. */}
      <section className={styles.hero}>
        <img
          src="/save-the-date.webp"
          alt=""
          className={styles.heroImg}
          loading="eager"
          fetchpriority="high"
        />
        <div className={styles.heroOverlay} aria-hidden="true" />

        {/* Floating poster doodles — pure decoration, no a11y impact. */}
        <Mushroom className={styles.heroDoodle1} size={68} color="#f06c33" spotColor="#f7f0e3" />
        <Mushroom className={styles.heroDoodle2} size={52} color="#f3b740" spotColor="#0d2a26" />
        <Hummingbird className={styles.heroDoodle3} size={110} color="#f3b740" />

        <div className={styles.heroInner}>
          <span className={styles.eyebrow}>Del Norte, Colorado · 2026</span>
          <h1 className={styles.heroTitle}>
            Rhythms<span className={styles.heroOn}>on the</span>Rio
          </h1>
          <p className={styles.heroDates}>{festival.dates}</p>
          <p className={styles.heroLede}>{festival.tagline}</p>
          <div className={styles.heroCtas}>
            <Button onClick={openTickets} variant="primary" size="lg">
              Buy Tickets
            </Button>
            <Button to="/lineup" variant="ghost" size="lg">
              See the Lineup
            </Button>
          </div>
        </div>
      </section>

      {/* MARQUEE — restless ticker of festival truths. Pure CSS
          animation, doubled markup so it loops seamlessly. */}
      <div className={styles.marquee} role="presentation">
        <div className={styles.marqueeTrack}>
          {[...Array(2)].map((_, i) => (
            <div className={styles.marqueeRow} key={i}>
              <span>4 Days · 1 Stage · 1 River</span>
              <Mushroom size={20} color="#f3b740" spotColor="#0d2a26" />
              <span>17 Bands</span>
              <Mushroom size={20} color="#f06c33" spotColor="#f7f0e3" />
              <span>Family Friendly</span>
              <Mushroom size={20} color="#f3b740" spotColor="#0d2a26" />
              <span>100% Volunteer Run</span>
              <Mushroom size={20} color="#f06c33" spotColor="#f7f0e3" />
              <span>Kids Play Music Because You Showed Up</span>
              <Mushroom size={20} color="#f3b740" spotColor="#0d2a26" />
            </div>
          ))}
        </div>
      </div>

      {/* INTRO — short statement of what makes ROTR ROTR. */}
      <section className={styles.intro}>
        <div className={styles.introInner}>
          <span className={styles.introEyebrow}>The Festival</span>
          <h2 className={styles.introTitle}>
            A camping music festival on the banks of the Rio Grande.
          </h2>
          <p className={styles.introBody}>
            Rhythms on the Rio is a benefit for the South Fork Music Association — a
            100% volunteer nonprofit that puts instruments and free lessons into the
            hands of kids across the San Luis Valley. Buy a ticket, camp by the
            river, and underwrite a year of music education in rural Colorado.
          </p>
          <Link to="/about" className={styles.introLink}>
            About the festival <IconArrow />
          </Link>
        </div>
        <div className={styles.introStats}>
          <div className={styles.stat}>
            <div className={styles.statNum}>17</div>
            <div className={styles.statLabel}>Bands</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statNum}>4</div>
            <div className={styles.statLabel}>Days</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statNum}>100%</div>
            <div className={styles.statLabel}>Volunteer-Run</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statNum}>1</div>
            <div className={styles.statLabel}>Mighty River</div>
          </div>
        </div>
      </section>

      {/* THE POSTER — full reveal of the 2026 art, treated as a
          centerpiece. Sunset gradient + scattered doodles set the
          70s-poster mood the artwork itself is asking for. */}
      <section className={styles.posterSection}>
        <SunBurst className={styles.posterSun} size={520} color="#f3b740" />
        <Mushroom className={styles.posterDoodleA} size={64} color="#f06c33" spotColor="#f7f0e3" />
        <Mushroom className={styles.posterDoodleB} size={48} color="#fff" spotColor="#f06c33" />
        <Hummingbird className={styles.posterDoodleC} size={130} color="#f06c33" />

        <div className={styles.posterInner}>
          <div className={styles.posterCopy}>
            <span className={styles.posterEyebrow}>The 2026 Poster</span>
            <h2 className={styles.posterTitle}>
              Far out
              <Squiggle className={styles.posterSquiggle} width={140} height={18} color="#0d2a26" />
              on the Rio.
            </h2>
            <p className={styles.posterBody}>
              Limited-edition art by our artist friends — mushrooms, hummingbirds,
              snakes, and a sunset over the river. Three sets of Leftover Salmon
              including a Jerry Garcia tribute. Two of the Stringdusters. Late-night
              Magoo. Late-night Sqwerv. Bring it on home.
            </p>
            <div className={styles.posterCtas}>
              <Button onClick={openTickets} variant="primary" size="lg">
                Buy Tickets
              </Button>
              <Button to="/lineup" variant="dark" size="lg">
                See the Full Lineup
              </Button>
            </div>
          </div>

          <a
            href="/posters/rotr-2026.png"
            target="_blank"
            rel="noopener"
            className={styles.posterImageLink}
            aria-label="Open full-size 2026 poster"
          >
            <img
              src="/posters/rotr-2026.png"
              alt="Rhythms on the Rio 2026 poster — psychedelic mushrooms, a hummingbird, and a snake under a sunset, with the lineup printed on a purple panel."
              loading="lazy"
            />
          </a>
        </div>
      </section>

      {/* HEADLINERS — big photo cards on cream. */}
      <section className={styles.headliners}>
        <div className={styles.sectionHead}>
          <span className={styles.sectionEyebrow}>2026 Headliners</span>
          <h2 className={styles.sectionTitle}>The shout from the main stage.</h2>
        </div>
        <div className={styles.headlinerGrid}>
          {headliners.map((a) => (
            <article key={a.id} className={styles.headlinerCard}>
              <div className={styles.headlinerImg}>
                <img src={a.photo} alt={a.name} loading="lazy" />
                <div className={styles.headlinerBadges}>
                  {a.sets > 1 && (
                    <span className={styles.setsBadge}>×{a.sets} Sets</span>
                  )}
                  {a.tribute && (
                    <span className={styles.tributeBadge}>{a.tribute}</span>
                  )}
                </div>
              </div>
              <h3 className={styles.headlinerName}>{a.name}</h3>
              <p className={styles.headlinerBlurb}>{a.blurb}</p>
            </article>
          ))}
        </div>
        <div className={styles.headlinerFoot}>
          <Link to="/lineup" className={styles.seeAll}>
            See all 17 artists <IconArrow />
          </Link>
        </div>
      </section>

      {/* SUPPORTING — tight grid, just names + small photos. */}
      <section className={styles.supporting}>
        <div className={styles.supportingGrid}>
          {supporting.map((a) => (
            <Link to="/lineup" key={a.id} className={styles.supportingCard}>
              <div className={styles.supportingImg}>
                <img src={a.photo} alt={a.name} loading="lazy" />
                {a.lateNight && <span className={styles.lateNight}>Late Night</span>}
              </div>
              <span className={styles.supportingName}>{a.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Snake stripe — divider between the lineup grid and press strip. */}
      <SnakeStripe height={28} color="#f06c33" color2="#f3b740" />

      {/* PRESS — quotes from publications. */}
      <section className={styles.press}>
        <div className={styles.pressInner}>
          <span className={styles.pressEyebrow}>From the Press</span>
          <ul className={styles.pressList}>
            {pressQuotes.slice(0, 4).filter((p) => p.quote).map((p) => (
              <li key={p.publication} className={styles.pressItem}>
                <p className={styles.pressQuote}>“{p.quote}”</p>
                <a
                  href={p.href}
                  target="_blank"
                  rel="noopener"
                  className={styles.pressSource}
                >
                  — {p.publication}
                </a>
              </li>
            ))}
          </ul>
          <Link to="/about/press" className={styles.pressMore}>
            Read more press <IconArrow />
          </Link>
        </div>
      </section>

      {/* MISSION — funnel to SFMA. */}
      <section
        className={styles.mission}
        style={{ backgroundImage: "url(/photos/sfma-donation.jpg)" }}
      >
        <div className={styles.missionOverlay} aria-hidden="true" />
        <div className={styles.missionInner}>
          <span className={styles.missionEyebrow}>South Fork Music Association</span>
          <h2 className={styles.missionTitle}>
            Every ticket buys a string, a reed, an hour of lessons.
          </h2>
          <p className={styles.missionBody}>
            SFMA is a 100% volunteer nonprofit that distributes instruments and free
            music lessons to children across the San Luis Valley. ROTR is its
            biggest fundraiser — show up, dance, support a kid you'll never meet
            who's about to find their instrument.
          </p>
          <Button to="/about/sfma" variant="primary" size="lg">
            Meet SFMA
          </Button>
        </div>
      </section>

      {/* FINAL CTA — keep tickets one tap away on the long scroll. */}
      <section className={styles.finalCta}>
        <Mushroom className={styles.finalDoodleA} size={56} color="#f06c33" spotColor="#f7f0e3" />
        <Mushroom className={styles.finalDoodleB} size={42} color="#f3b740" spotColor="#0d2a26" />
        <h2 className={styles.finalTitle}>{festival.dates}</h2>
        <p className={styles.finalLede}>
          {festival.address}
          <br />
          See you on the river.
        </p>
        <Button onClick={openTickets} variant="primary" size="lg">
          Buy Tickets
        </Button>
      </section>
    </>
  );
}
